import { DestroyRef, inject, Injectable, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { ActivatedRoute } from '@angular/router'
import { FormGroup } from '@angular/forms'
import { catchError, distinctUntilChanged, EMPTY, filter, map, Observable, of, Subject, switchMap, take, tap } from 'rxjs'
import { CertificateGeneralInfoService } from '@features/certificate/services/certificate-general-info/certificate-general-info.service'
import { CertificateFormService } from '@features/certificate/services/certificate-form/certificate-form.service'
import { GlobalErrorService } from '@services/global-error.service'
import { CertificateApiResponse } from '@features/certificate/models/certificate-general-info/certificate-general-info.model'
import { CertificateDocumentsApiResponse } from '@features/certificate/models/certificate-documents/certificate-documents.model'
import { CertificateMetaDataForm } from '@features/certificate/models/certificate-meta-data/certificate-meta-data.model'
import { CertificateDetailsForm } from '@features/certificate/models/certificate-details/certificate-details.model'
import { CertificateContractDetailsForm } from '@features/certificate/models/certificate-contract-details/certificate-contract-details.model'
import { HullSparesCoverProvisionsForm } from '@features/certificate/models/hull-spares-cover-provisions/hull-spares-cover-provisions.model'
import {
  AircraftTypeLimitRow,
  LiabilityCoverProvisionsForm
} from '@features/certificate/models/liability-cover-provisions/liability-cover-provisions.model'
import { FinalProvisionsForm } from '@features/certificate/models/final-provisions/final-provisions.model'
import { CertificateAcrForm } from '@features/certificate/models/certificate-acr/certificate-acr.model'
import { PeriodForm } from '@features/insured/models/period/period.model'
import { PolicyForm } from '@features/insured/models/policy/policy.model'
import { CertificateFormBundle } from '@features/certificate/models/certificate-shared.model'
import {
  CertCertificateHolderApiResponse,
  CertificateHolderApiResponse,
  CertificateHolderEmail,
  CertificateHolderForm
} from '@features/certificate-holder/models/certificate-holder.model'
import { hasActualValue } from '@core/utils/form-utils'
import { PeriodFormService } from '@features/insured/services/period/period-form.service'
import { PolicyFormService } from '@features/insured/services/policy/policy-form.service'
import { CertificateMapperService } from '@features/certificate/services/certificate-mapper/certificate-mapper.service'
import { InsuredDataFacade } from '@features/insured/services/insured-data.facade'
import { MemberService } from '@features/insured/services/member/member.service'
import { InsuredCertificatesStateService } from '@app/layout/sidebar/services/insured-certificates-state.service'
import { SelectedInsuredService } from '@app/layout/sidebar/services/selected-insured.service'
import { FurtherInsuredLimitsService } from '@features/certificate/services/further-insured-limits/further-insured-limits.service'

// Case-insensitive ordering of recipients and their addresses, created once and shared across sorts.
const RECIPIENT_COLLATOR = new Intl.Collator(undefined, { sensitivity: 'base' })

@Injectable()
export class CertificateManagerService {
  public readonly metaDataForm: FormGroup<CertificateMetaDataForm>
  public readonly detailsForm: FormGroup<CertificateDetailsForm>
  public readonly contractDetailsForm: FormGroup<CertificateContractDetailsForm>
  public readonly hullSparesCoverProvisionsForm: FormGroup<HullSparesCoverProvisionsForm>
  public readonly liabilityCoverProvisionsForm: FormGroup<LiabilityCoverProvisionsForm>
  public readonly finalProvisionsForm: FormGroup<FinalProvisionsForm>
  public readonly certificateHolderForm: FormGroup<CertificateHolderForm>
  public readonly acrForm: FormGroup<CertificateAcrForm>
  public readonly periodForm: FormGroup<PeriodForm>
  public readonly policyForm: FormGroup<PolicyForm>
  public readonly documents = signal<CertificateDocumentsApiResponse[]>([])
  public readonly certificateHolders = signal<CertCertificateHolderApiResponse[]>([])
  public readonly additionalCertificateRecipients = signal<CertCertificateHolderApiResponse[]>([])
  private readonly recipientListsEditedSubject = new Subject<void>()
  /** Emits when the user edits the certificate holders or additional recipients, never on hydration. */
  public readonly recipientListsEdited$ = this.recipientListsEditedSubject.asObservable()
  public readonly savedUuid = signal<string | null>(null)
  public readonly fleetListAsAttachmentLocked = signal(false)
  private readonly route = inject(ActivatedRoute)
  private readonly destroyRef = inject(DestroyRef)
  private readonly generalInfoService = inject(CertificateGeneralInfoService)
  private readonly formService = inject(CertificateFormService)
  private readonly globalErrorService = inject(GlobalErrorService)
  private readonly periodFormService = inject(PeriodFormService)
  private readonly policyFormService = inject(PolicyFormService)
  private readonly mapperService = inject(CertificateMapperService)
  private readonly insuredDataFacade = inject(InsuredDataFacade)
  private readonly memberService = inject(MemberService)
  private readonly sidebarState = inject(InsuredCertificatesStateService)
  private readonly selectedInsured = inject(SelectedInsuredService)
  private readonly furtherInsuredLimitsService = inject(FurtherInsuredLimitsService)
  private readonly policies = this.insuredDataFacade.policies
  private uuid: string | null = null
  private insuredUuid: string | null = null
  private hydrating = false

  constructor() {
    this.metaDataForm = this.formService.buildMetaDataForm()
    this.detailsForm = this.formService.buildDetailsForm()
    this.contractDetailsForm = this.formService.buildContractDetailsForm()
    this.hullSparesCoverProvisionsForm = this.formService.buildHullSparesCoverProvisionsForm()
    this.liabilityCoverProvisionsForm = this.formService.buildLiabilityCoverProvisionsForm()
    this.finalProvisionsForm = this.formService.buildFinalProvisionsForm()
    this.certificateHolderForm = this.formService.buildCertificateHolderForm()
    this.acrForm = this.formService.buildAcrForm()
    this.periodForm = this.periodFormService.buildForm(true)
    this.policyForm = this.policyFormService.buildForm(true)

    this.loadInitialData()
    this.loadPeriods()
    this.setupPeriodAndPolicyListeners()
    this.setupHullFleetlistFromLiabilityCover()
    this.setupAircraftTypeLimitsFromInsured()
  }

  public loadCertificate(uuid: string): void {
    this.generalInfoService.getById(uuid).pipe(take(1)).subscribe({
      next: (response) => {
        this.uuid = response.uuid
        this.savedUuid.set(response.uuid)
        const bundle: CertificateFormBundle = {
          metaData: this.metaDataForm,
          details: this.detailsForm,
          contractDetails: this.contractDetailsForm,
          hullSparesCoverProvisions: this.hullSparesCoverProvisionsForm,
          liabilityCoverProvisions: this.liabilityCoverProvisionsForm,
          finalProvisions: this.finalProvisionsForm,
          certificateHolder: this.certificateHolderForm,
          acr: this.acrForm,
          period: this.periodForm,
          policy: this.policyForm,
        }
        // Suppress the member/period/policy listeners during hydration so they don't clear/reload
        // the policy form and discard the values just patched from the response.
        this.hydrating = true
        this.formService.patchAllForms(response, bundle)
        this.hydrating = false
        // Server-driven hydration: patching triggers the member/period listeners (which call
        // `clearPolicy()` and mark the policy form dirty). Reset every form to pristine so the
        // autosave dirty-gate doesn't fire a PUT straight after the initial GET.
        this.markAllFormsPristine()
        // Refresh the policy dropdown options without patching (and clobbering) the hydrated policy
        // form; the manager's own `loadPolicies()` is skipped while hydrating.
        this.refreshPolicyOptions()
        this.documents.set(response.documents ?? [])
        this.certificateHolders.set(response.certificateHolders ?? [])
        this.additionalCertificateRecipients.set(response.additionalRecipients ?? [])
        const memberUuid = response.member?.uuid
        if (hasActualValue(memberUuid)) {
          this.selectedInsured.select(memberUuid)
        }
      }, error: () => this.globalErrorService.reportError({
        subsystemName: 'Certificate Loading', description: 'The Certificate could not be loaded.'
      })
    })
  }

  public save(): void {
    this.performSave$().subscribe()
  }

  public performSave$(): Observable<CertificateApiResponse> {
    const payload = this.mapperService.buildPayload(
      this.uuid,
      this.insuredUuid,
      {
        metaData: this.metaDataForm,
        details: this.detailsForm,
        contractDetails: this.contractDetailsForm,
        hull: this.hullSparesCoverProvisionsForm,
        liability: this.liabilityCoverProvisionsForm,
        finalProv: this.finalProvisionsForm,
        policy: this.policyForm,
        period: this.periodForm,
      },
      {
        holders: this.certificateHolders(),
        acr: this.additionalCertificateRecipients(),
        documents: this.documents()
      }
    )

    return this.generalInfoService.save(payload).pipe(
      take(1),
      tap(response => {
        if (this.uuid === null && response.uuid !== null && response.uuid !== undefined) {
          this.uuid = response.uuid
          this.savedUuid.set(response.uuid)
        }
        this.applySavedContractPartyCompanies(payload, response)
        // Refresh the certificate sidebar list so a newly created (or renamed) certificate
        // shows up immediately, without requiring a manual page reload.
        this.sidebarState.reload().subscribe()
      }),
      catchError(() => {
        this.globalErrorService.reportError({
          subsystemName: 'Certificate Saving', description: 'The Certificate could not be saved.'
        })
        return EMPTY
      })
    )
  }

  public isCreateMode(): boolean {
    const uuid = this.route.snapshot.params['uuid'] as string | undefined
    return typeof uuid !== 'string' || uuid === ''
  }

  public addCertificateHolder(holder: CertificateHolderApiResponse): void {
    const current = this.certificateHolders()
    const alreadyAdded = current.some(ch => ch.uuid === holder.uuid)
    if (!alreadyAdded) {
      this.editCertificateHolders([...current,
        {
          uuid: holder.uuid,
          companyName: holder.companyName,
          country: holder.country,
          freetext: holder.freetext,
          certificateHolderRecipientAddresses: holder.recipientAddresses?.map(a => ({ uuid: a.uuid, email: a.email, emailValid: true })) ?? []
        }])
    }
  }

  public addCertificateHolderWithoutEmails(holder: CertificateHolderApiResponse): void {
    const current = this.certificateHolders()
    const alreadyAdded = current.some(ch => ch.uuid === holder.uuid)
    if (!alreadyAdded) {
      this.editCertificateHolders([...current,
        { uuid: holder.uuid, companyName: holder.companyName, country: holder.country, freetext: holder.freetext, certificateHolderRecipientAddresses: [] }])
    }
  }

  public addAcr(holder: CertificateHolderApiResponse): void {
    const current = this.additionalCertificateRecipients()
    const alreadyAdded = current.some(acr => acr.uuid === holder.uuid)
    if (!alreadyAdded) {
      this.editAdditionalCertificateRecipients([...current,
        {
          uuid: holder.uuid,
          companyName: holder.companyName,
          country: holder.country,
          freetext: holder.freetext,
          certificateHolderRecipientAddresses: holder.recipientAddresses?.map(a => ({ uuid: a.uuid, email: a.email, emailValid: true })) ?? []
        }])
    }
  }

  public addAcrWithoutEmails(holder: CertificateHolderApiResponse): void {
    const current = this.additionalCertificateRecipients()
    const alreadyAdded = current.some(acr => acr.uuid === holder.uuid)
    if (!alreadyAdded) {
      this.editAdditionalCertificateRecipients([...current,
        { uuid: holder.uuid, companyName: holder.companyName, country: holder.country, freetext: holder.freetext, certificateHolderRecipientAddresses: [] }])
    }
  }

  public addCertificateHolderSingleEmail(event: { holder: CertificateHolderApiResponse; address: CertificateHolderEmail }): void {
    const current = this.certificateHolders()
    const existingIndex = current.findIndex(ch => ch.uuid === event.holder.uuid)
    if (existingIndex >= 0) {
      const existing = current[existingIndex]!
      const emails = existing.certificateHolderRecipientAddresses ?? []
      if (!emails.some(e => e.email === event.address.email)) {
        this.editCertificateHolders(current.map((ch, i) => i === existingIndex ? {
          ...ch,
          certificateHolderRecipientAddresses: [...emails, { uuid: event.address.uuid, email: event.address.email, emailValid: true }]
        } : ch))
      }
    } else {
      this.editCertificateHolders([...current,
        {
          uuid: event.holder.uuid,
          companyName: event.holder.companyName,
          country: event.holder.country,
          freetext: event.holder.freetext,
          certificateHolderRecipientAddresses: [{ uuid: event.address.uuid, email: event.address.email, emailValid: true }]
        }])
    }
  }

  public removeCertificateHolder(holderIndex: number): void {
    const current = this.certificateHolders()
    this.editCertificateHolders(current.filter((_, i) => i !== holderIndex))
  }

  public removeCertificateHolderAddress(event: { holderIndex: number; addressIndex: number }): void {
    const current = this.certificateHolders()
    this.editCertificateHolders(current.map((holder, i) => {
      if (i !== event.holderIndex) return holder
      return { ...holder, certificateHolderRecipientAddresses: (holder.certificateHolderRecipientAddresses ?? []).filter((_, ai) => ai !== event.addressIndex) }
    }))
  }

  public addAcrSingleEmail(event: { holder: CertificateHolderApiResponse; address: CertificateHolderEmail }): void {
    const current = this.additionalCertificateRecipients()
    const existingIndex = current.findIndex(acr => acr.uuid === event.holder.uuid)
    if (existingIndex >= 0) {
      const existing = current[existingIndex]!
      const emails = existing.certificateHolderRecipientAddresses ?? []
      if (!emails.some(e => e.email === event.address.email)) {
        this.editAdditionalCertificateRecipients(current.map((acr, i) => i === existingIndex ? {
          ...acr,
          certificateHolderRecipientAddresses: [...emails, { uuid: event.address.uuid, email: event.address.email, emailValid: true }]
        } : acr))
      }
    } else {
      this.editAdditionalCertificateRecipients([...current,
        {
          uuid: event.holder.uuid,
          companyName: event.holder.companyName,
          country: event.holder.country,
          freetext: event.holder.freetext,
          certificateHolderRecipientAddresses: [{ uuid: event.address.uuid, email: event.address.email, emailValid: true }]
        }])
    }
  }

  public removeAcr(holderIndex: number): void {
    const current = this.additionalCertificateRecipients()
    this.editAdditionalCertificateRecipients(current.filter((_, i) => i !== holderIndex))
  }

  public removeAcrAddress(event: { holderIndex: number; addressIndex: number }): void {
    const current = this.additionalCertificateRecipients()
    this.editAdditionalCertificateRecipients(current.map((recipient, i) => {
      if (i !== event.holderIndex) return recipient
      return {
        ...recipient,
        certificateHolderRecipientAddresses: (recipient.certificateHolderRecipientAddresses ?? []).filter((_, ai) => ai !== event.addressIndex)
      }
    }))
  }

  // User edits of the two lists go through these setters so the autosave can tell them apart from
  // hydration, which sets the signals directly. Both lists are kept sorted alphabetically by company name,
  // and each recipient's addresses by email, mirroring the order the backend returns.
  private editCertificateHolders(holders: CertCertificateHolderApiResponse[]): void {
    this.certificateHolders.set(this.sortRecipients(holders))
    this.recipientListsEditedSubject.next()
  }

  private editAdditionalCertificateRecipients(recipients: CertCertificateHolderApiResponse[]): void {
    this.additionalCertificateRecipients.set(this.sortRecipients(recipients))
    this.recipientListsEditedSubject.next()
  }

  private sortRecipients(holders: CertCertificateHolderApiResponse[]): CertCertificateHolderApiResponse[] {
    return holders
      .map(holder => ({
        ...holder,
        certificateHolderRecipientAddresses: [...(holder.certificateHolderRecipientAddresses ?? [])]
          .sort((a, b) => RECIPIENT_COLLATOR.compare(a.email, b.email))
      }))
      .sort((a, b) => RECIPIENT_COLLATOR.compare(a.companyName, b.companyName))
  }

  /**
   * The backend assigns the ids and fills in the EUAT audit columns (created/edited on/by) of the contract
   * party companies on save, so they are taken over from the response. This happens silently: the autosave
   * would otherwise treat the server-owned fields as a fresh user edit. A response is ignored once the user
   * has already replaced the list again, so an in-flight save cannot overwrite the newer rows.
   */
  private applySavedContractPartyCompanies(payload: CertificateApiResponse, response: CertificateApiResponse): void {
    const control = this.contractDetailsForm.controls.contractPartiesEuat
    if (response.contractPartiesEuat === undefined || control.value !== payload.contractPartiesEuat) {
      return
    }
    control.setValue(response.contractPartiesEuat ?? [], { emitEvent: false })
  }

  private markAllFormsPristine(): void {
    for (const form of [
      this.metaDataForm,
      this.detailsForm,
      this.contractDetailsForm,
      this.hullSparesCoverProvisionsForm,
      this.liabilityCoverProvisionsForm,
      this.finalProvisionsForm,
      this.certificateHolderForm,
      this.acrForm,
      this.periodForm,
      this.policyForm,
    ]) {
      form.markAsPristine()
    }
  }

  private loadPeriods(): void {
    this.insuredDataFacade.loadPeriods().subscribe({
      next: (periods) => {
        const currentPeriodUuid = this.periodForm.controls.uuid.value
        const found = periods.find(p => p.uuid === currentPeriodUuid)
        if (found) {
          this.periodForm.patchValue(found)
        } else if (this.isCreateMode()) {
          const activePeriod = periods.find(p => p.active) ?? periods[0]
          if (activePeriod) {
            this.periodForm.patchValue(activePeriod)
          }
        }
      },
      error: () => {
        this.globalErrorService.reportError({
          subsystemName: 'Period Loading',
          description: 'The available periods could not be loaded.'
        })
      }
    })
  }

  private loadPolicies(memberUuid: string, periodUuid: string): void {
    this.insuredDataFacade.loadPolicies(memberUuid, periodUuid).subscribe({
      next: (policies) => {
        const currentPolicyUuid = this.policyForm.controls.uuid.value
        const found = policies.find(p => p.uuid === currentPolicyUuid)
        if (found) {
          this.policyForm.patchValue(found)
          this.policyForm.markAsPristine()
        } else if (this.isCreateMode()) {
          const firstPolicy = policies[0]
          if (firstPolicy) {
            this.policyForm.patchValue(firstPolicy)
            this.policyForm.markAsPristine()
          }
        } else {
          this.policyForm.controls.uuid.markAsTouched()
          this.policyForm.controls.uuid.markAsDirty()
          this.policyForm.controls.uuid.updateValueAndValidity()
        }
      },
      error: () => {
        this.globalErrorService.reportError({
          subsystemName: 'Policy Loading',
          description: 'The policies for this member and period could not be loaded.'
        })
      }
    })
  }

  private setupPeriodAndPolicyListeners(): void {
    this.metaDataForm.controls.member.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map(member => member?.uuid ?? null),
        distinctUntilChanged()
      )
      .subscribe(memberUuid => {
        if (this.hydrating) return
        this.clearPolicy()

        if (hasActualValue(memberUuid)) {
          this.insuredDataFacade.policyOptions.set([])

          const periodUuid = this.periodForm.controls.uuid.value
          if (hasActualValue(periodUuid)) {
            this.loadPolicies(memberUuid, periodUuid)
          }
        }
      })

    this.periodForm.controls.uuid.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged())
      .subscribe((periodUuid: string | null) => {
        if (this.hydrating) return
        this.detailsForm.controls.periodId.setValue(periodUuid)
        this.clearPolicy()

        if (hasActualValue(periodUuid)) {
          const memberUuid = this.metaDataForm.controls.member.value?.uuid
          if (hasActualValue(memberUuid)) {
            this.loadPolicies(memberUuid, periodUuid)
          }
        }
      })

    this.policyForm.controls.uuid.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged())
      .subscribe((policyUuid: string | null) => {
        if (this.hydrating) return
        this.detailsForm.controls.policyId.setValue(policyUuid)
        if (hasActualValue(policyUuid)) {
          const selectedPolicy = this.policies().find(p => p.uuid === policyUuid)
          if (hasActualValue(selectedPolicy)) {
            this.policyForm.patchValue(selectedPolicy, { emitEvent: false })
            this.policyForm.markAsPristine()
          }
        }
      })
  }

  private setupHullFleetlistFromLiabilityCover(): void {
    const showLimitsOnAttachedFleetListCtrl = this.liabilityCoverProvisionsForm.controls.liabilityAvn52eLimitsFleetList
    const addFleetListAsAttachmentCtrl = this.hullSparesCoverProvisionsForm.controls.addFleetListAsAttachment

    showLimitsOnAttachedFleetListCtrl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged())
      .subscribe(checked => {
        this.fleetListAsAttachmentLocked.set(checked === true)
        if (checked === true) {
          addFleetListAsAttachmentCtrl.setValue(true, { emitEvent: false })
        }
        if (checked === false) {
          addFleetListAsAttachmentCtrl.setValue(false, { emitEvent: false })
        }
      })
  }

  /**
   * Ticking "Show Limits per Aircraft Type" takes over the insured's AVN52E limits per aircraft type once, which are
   * stored with the certificate and edited there from then on; unticking it removes them again.
   */
  private setupAircraftTypeLimitsFromInsured(): void {
    const form = this.liabilityCoverProvisionsForm
    form.controls.liabilityLimitsPerAircraftType.valueChanges
      .pipe(
        // Before the hydration check, so a loaded certificate's value counts as the current one: enabling or
        // disabling the control for a category re-emits it and must not replace the stored limits.
        distinctUntilChanged(),
        filter(() => !this.hydrating),
        // An untick while the limits are still loading wins over them.
        switchMap(checked => checked === true ? this.loadInsuredAircraftTypeLimits() : of([])),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(rows => {
        // Marked dirty first: the autosave only picks up a change it sees on an already dirty form.
        form.controls.liabilityLimitAmountAircraft.markAsDirty()
        form.controls.liabilityAircraftType.markAsDirty()
        this.formService.setAircraftTypeLimits(form, rows)
      })
  }

  private loadInsuredAircraftTypeLimits(): Observable<AircraftTypeLimitRow[]> {
    const policyId = this.detailsForm.controls.policyId.value
    if (!hasActualValue(policyId)) {
      return of([])
    }
    return this.furtherInsuredLimitsService.loadAircraftLimits(policyId).pipe(
      map(limits => limits.map(limit => ({
        aircraftType: limit.aircraftType ?? '',
        amount: limit.limitValue,
        currency: limit.limitCurrency
      }))),
      catchError(() => {
        this.globalErrorService.reportError({
          subsystemName: 'Aircraft Type Limits Loading',
          description: 'The limits per aircraft type of the insured could not be loaded.'
        })
        return EMPTY
      })
    )
  }

  private refreshPolicyOptions(): void {
    const memberUuid = this.metaDataForm.controls.member.value?.uuid
    const periodUuid = this.periodForm.controls.uuid.value
    if (hasActualValue(memberUuid) && hasActualValue(periodUuid)) {
      this.insuredDataFacade.loadPolicies(memberUuid, periodUuid).subscribe({
        error: () => this.globalErrorService.reportError({
          subsystemName: 'Policy Loading',
          description: 'The policies for this member and period could not be loaded.'
        })
      })
    }
  }

  private clearPolicy(): void {
    this.detailsForm.controls.policyId.setValue(null)
    this.policyForm.reset(undefined, { emitEvent: false })
    this.policyForm.controls.uuid.markAsTouched()
    this.policyForm.controls.uuid.markAsDirty()
    this.policyForm.controls.uuid.updateValueAndValidity({ emitEvent: false })
  }

  private loadInitialData(): void {
    this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const uuid = params['uuid'] as string | undefined
      if (typeof uuid === 'string' && uuid !== '') {
        this.loadCertificate(uuid)
      } else {
        const insuredUuid = this.route.snapshot.queryParamMap?.get('insuredUuid') ?? null
        this.insuredUuid = insuredUuid !== null && insuredUuid !== '' ? insuredUuid : null
        if (this.insuredUuid !== null) {
          this.prefillMember(this.insuredUuid)
        }
      }
    })
  }

  private prefillMember(insuredUuid: string): void {
    this.memberService.getById(insuredUuid).pipe(take(1)).subscribe({
      next: (member) => {
        const name = member.name?.externalValue ?? member.name?.localValue ?? null
        this.metaDataForm.controls.member.setValue({
          uuid: insuredUuid,
          name,
          coverageType: this.metaDataForm.controls.member.value?.coverageType ?? null
        })
      },
      error: () => this.globalErrorService.reportError({
        subsystemName: 'Insured Loading', description: 'The insured could not be loaded.'
      })
    })
  }
}
